/**
 * Geolocation utilities for location-based sorting
 */

export interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
}

export interface LocationError {
  code: number;
  message: string;
}

/**
 * Get the user's current location using the browser's geolocation API
 */
export const getCurrentLocation = (): Promise<Location> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 300000, // 5 minutes
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
      },
      (error) => {
        const errorMessage = getGeolocationErrorMessage(error);
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

/**
 * Watch the user's location for continuous updates
 */
export const watchLocation = (
  onUpdate: (location: Location) => void,
  onError: (error: Error) => void
): number => {
  if (!navigator.geolocation) {
    onError(new Error('Geolocation is not supported by this browser'));
    return -1;
  }

  const options: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000, // 1 minute
  };

  return navigator.geolocation.watchPosition(
    (position) => {
      onUpdate({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      });
    },
    (error) => {
      const errorMessage = getGeolocationErrorMessage(error);
      onError(new Error(errorMessage));
    },
    options
  );
};

/**
 * Clear location watching
 */
export const clearLocationWatch = (watchId: number): void => {
  if (navigator.geolocation && watchId !== -1) {
    navigator.geolocation.clearWatch(watchId);
  }
};

/**
 * Calculate the distance between two points using the Haversine formula
 * Returns distance in kilometers
 */
export const calculateDistance = (
  point1: { latitude: number; longitude: number },
  point2: { latitude: number; longitude: number }
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(point2.latitude - point1.latitude);
  const dLon = toRadians(point2.longitude - point1.longitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.latitude)) *
      Math.cos(toRadians(point2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return parseFloat(distance.toFixed(2));
};

/**
 * Convert degrees to radians
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Get human-readable error message from GeolocationPositionError
 */
const getGeolocationErrorMessage = (error: GeolocationPositionError): string => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location access denied by user';
    case error.POSITION_UNAVAILABLE:
      return 'Location information unavailable';
    case error.TIMEOUT:
      return 'Location request timed out';
    default:
      return 'Unknown location error occurred';
  }
};

/**
 * Sort artist fairs by distance from user's location
 */
export const sortByDistance = <T extends { address?: { latitude?: number; longitude?: number } }>(
  items: T[],
  userLocation: Location
): (T & { distance?: number })[] => {
  return items.map(item => {
    if (item.address?.latitude && item.address?.longitude) {
      const distance = calculateDistance(
        userLocation,
        {
          latitude: item.address.latitude,
          longitude: item.address.longitude,
        }
      );
      return { ...item, distance };
    }
    return { ...item, distance: Infinity };
  }).sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
};

/**
 * Format distance for display
 */
export const formatDistance = (distance: number): string => {
  if (distance === Infinity || isNaN(distance)) {
    return 'Distance unknown';
  }

  if (distance < 1) {
    return `${Math.round(distance * 1000)}m away`;
  }

  return `${distance.toFixed(1)}km away`;
};

/**
 * Check if geolocation is supported and permission is granted
 */
export const checkGeolocationSupport = async (): Promise<{
  supported: boolean;
  permission: 'granted' | 'denied' | 'prompt' | 'unknown';
}> => {
  if (!navigator.geolocation) {
    return { supported: false, permission: 'unknown' };
  }

  if (!navigator.permissions) {
    return { supported: true, permission: 'unknown' };
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return {
      supported: true,
      permission: permission.state as 'granted' | 'denied' | 'prompt',
    };
  } catch {
    return { supported: true, permission: 'unknown' };
  }
};